/// ConsolePanel: Engine event log with filtering and auto-scroll.
/// Shows timestamped events with severity coloring, type/severity filtering,
/// auto-scroll toggle, and clear functionality.

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useEngineStore } from "@/stores/engineStore";
import { getEventSeverity, type EventSeverity } from "@/types/properties";
import type { EngineEventLog } from "@/types/engine";

// ---------------------------------------------------------------------------
// Severity filter state
// ---------------------------------------------------------------------------

type SeveritySet = Record<EventSeverity, boolean>;

const DEFAULT_SEVERITY: SeveritySet = {
  info: true,
  warning: true,
  error: true,
};

// ---------------------------------------------------------------------------
// ConsolePanel component
// ---------------------------------------------------------------------------

export function ConsolePanel() {
  const events = useEngineStore((s) => s.events);
  const clearEvents = useEngineStore((s) => s.clearEvents);

  const [severityFilter, setSeverityFilter] = useState<SeveritySet>(DEFAULT_SEVERITY);
  const [typeFilter, setTypeFilter] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Toggle a severity filter
  const toggleSeverity = useCallback((severity: EventSeverity) => {
    setSeverityFilter((prev) => ({
      ...prev,
      [severity]: !prev[severity],
    }));
  }, []);

  // Filter events by severity and type
  const filtered = useMemo(() => {
    const lowerType = typeFilter.toLowerCase();
    return events.filter((e) => {
      const severity = getEventSeverity(e.eventType);
      if (!severityFilter[severity]) return false;
      if (lowerType && !e.eventType.toLowerCase().includes(lowerType)) return false;
      return true;
    });
  }, [events, severityFilter, typeFilter]);

  // Auto-scroll to bottom (events are newest-first, so scroll to top)
  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [events.length, autoScroll]);

  return (
    <div className="flex flex-col h-full">
      {/* Filter toolbar */}
      <div className="flex items-center gap-2 border-b border-zinc-700 px-3 py-1 shrink-0">
        {/* Severity toggles */}
        <SeverityToggle
          severity="info"
          label="Info"
          active={severityFilter.info}
          onClick={() => toggleSeverity("info")}
          activeClass="bg-zinc-600 text-zinc-200"
        />
        <SeverityToggle
          severity="warning"
          label="Warn"
          active={severityFilter.warning}
          onClick={() => toggleSeverity("warning")}
          activeClass="bg-yellow-900 text-yellow-300"
        />
        <SeverityToggle
          severity="error"
          label="Error"
          active={severityFilter.error}
          onClick={() => toggleSeverity("error")}
          activeClass="bg-red-900 text-red-300"
        />

        <div className="h-4 w-px bg-zinc-700" />

        {/* Type search */}
        <input
          type="text"
          placeholder="Filter by type..."
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="flex-1 bg-transparent text-xs text-zinc-300 placeholder-zinc-500 focus:outline-none"
        />

        <div className="h-4 w-px bg-zinc-700" />

        {/* Auto-scroll toggle */}
        <button
          type="button"
          className={`text-xs transition-colors ${autoScroll ? "text-blue-400" : "text-zinc-500 hover:text-zinc-300"}`}
          onClick={() => setAutoScroll(!autoScroll)}
        >
          Auto-scroll
        </button>

        {/* Clear button */}
        <button
          type="button"
          className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
          onClick={clearEvents}
        >
          Clear
        </button>
      </div>

      {/* Event list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto font-mono text-xs min-h-0">
        {filtered.length === 0 && (
          <div className="flex items-center justify-center h-full text-zinc-500">No events</div>
        )}
        {filtered.map((event) => (
          <EventRow key={event.id} event={event} />
        ))}
      </div>

      {/* Footer with count */}
      <div className="border-t border-zinc-700 px-3 py-0.5 text-xs text-zinc-500 shrink-0">
        {filtered.length} / {events.length} events
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SeverityToggle
// ---------------------------------------------------------------------------

function SeverityToggle({
  label,
  active,
  onClick,
  activeClass,
}: {
  severity: EventSeverity;
  label: string;
  active: boolean;
  onClick: () => void;
  activeClass: string;
}) {
  return (
    <button
      type="button"
      className={`text-xs px-2 py-0.5 rounded transition-colors ${
        active ? activeClass : "text-zinc-500 hover:text-zinc-400"
      }`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// EventRow
// ---------------------------------------------------------------------------

const SEVERITY_COLORS: Record<EventSeverity, string> = {
  info: "text-zinc-400",
  warning: "text-yellow-400",
  error: "text-red-400",
};

function EventRow({ event }: { event: EngineEventLog }) {
  const time = new Date(event.timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  });

  const severity = getEventSeverity(event.eventType);
  const colorClass = SEVERITY_COLORS[severity];

  return (
    <div className="flex items-start gap-2 border-b border-zinc-800 px-3 py-1 hover:bg-zinc-800/50">
      <span className="text-zinc-500 shrink-0">{time}</span>
      <span className={`shrink-0 w-28 truncate ${colorClass}`}>{event.eventType}</span>
      <span className="text-zinc-500 shrink-0 w-20 truncate" title={event.workerId}>
        {event.workerId}
      </span>
      <span className="text-zinc-400 truncate">{formatPayload(event.payload)}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payload formatter
// ---------------------------------------------------------------------------

function formatPayload(payload: Record<string, unknown>): string {
  const entries = Object.entries(payload);
  if (entries.length === 0) return "";

  return entries
    .map(([key, value]) => {
      if (typeof value === "string") return `${key}="${value}"`;
      if (typeof value === "number" || typeof value === "boolean") return `${key}=${value}`;
      return `${key}=${JSON.stringify(value)}`;
    })
    .join(" ");
}
